import { ReactNode } from "react";
import { LogOut, User as UserIcon, Activity } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { ThemeToggle } from "@/components/ui/ThemeToggle";

interface LayoutProps {
  children: ReactNode;
  /** Optional center slot for things like the symbol search */
  center?: ReactNode;
}

export function Layout({ children, center }: LayoutProps) {
  const { user, logout } = useAuth();

  return (
    <div className="flex h-screen flex-col bg-bg-deep">
      {/* Top bar */}
      <header className="flex h-12 shrink-0 items-center justify-between border-b border-border bg-bg-panel px-4">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <Activity className="h-5 w-5 text-accent" strokeWidth={2.5} />
            <span className="text-sm font-bold tracking-wide text-text-primary">
              Democrat<span className="text-accent">Charts</span>
            </span>
          </div>
          <span className="text-text-muted">·</span>
          <span className="text-2xs uppercase tracking-widest text-text-muted">
            replay desk
          </span>
        </div>

        <div className="flex-1 px-8">{center}</div>

        <div className="flex items-center gap-3">
          {user && (
            <div className="flex items-center gap-2 text-xs text-text-secondary">
              <UserIcon className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">{user.full_name}</span>
              <span className="rounded bg-bg-elevated px-1.5 py-0.5 text-2xs uppercase tracking-wider text-text-muted">
                {user.role}
              </span>
            </div>
          )}
          <ThemeToggle />
          <button
            onClick={() => logout()}
            className="flex items-center gap-1.5 rounded-md px-2 py-1 text-xs text-text-secondary transition-colors hover:bg-bg-elevated hover:text-text-primary"
            aria-label="Logout"
          >
            <LogOut className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">Logout</span>
          </button>
        </div>
      </header>

      {/* Main content fills remaining height */}
      <main className="flex-1 overflow-hidden">{children}</main>
    </div>
  );
}
